import { appConfig } from '../../config/appConfig';
import { arStarted, deviceUnsupported, permissionDenied } from '../../state/appState';
import { logger } from '../../utils/logger';
import { isARSupported } from '../../utils/deviceSupport';
import { startARSession, stopARSession } from '../../ar/sessionManager';
import { acquireWakeLock, releaseWakeLock } from '../../utils/wakeLock';

// AR shell — AR_STARTING state.
// Renders the persistent AR container, starts the XR8 session, then
// transitions to SCANNING. If the device cannot support AR, transitions to
// UNSUPPORTED instead.

export function renderARScreen(root: HTMLElement): void {
  root.innerHTML = `
    <div class="ar-shell ar-shell--fade-in" id="ar-shell">
      <!-- XR8 renders camera feed directly onto this canvas -->
      <canvas class="ar-viewport" id="ar-canvas" aria-hidden="true"></canvas>

      <!-- Persistent top-left logo shown across all AR states -->
      <div class="ar-logo" aria-label="Sag Harbor Whaling and Historical Museum">
        <img src="/assets/logo.png" alt="Museum Logo" />
        <!--
        <div class="ar-logo__mark"></div>
        <span class="ar-logo__text">Sag Harbor<br>Whaling &amp; Historical Museum</span>
        -->
      </div>

      <!-- SLAM tracking status dot — top-right, driven by reality.trackingstatus -->
      ${appConfig.debug ? '<div class="slam-status-dot" id="slam-status-dot"></div>' : ''}
      ${
        appConfig.debug
          ? '<div class="ar-debug-panel" id="ar-debug-panel" aria-hidden="true"></div>'
          : ''
      }

      <!-- Overlay slot — instructional text, modals, etc. inserted here -->
      <div class="ar-overlay-slot" id="ar-overlay-slot"></div>
    </div>
  `;

  if (!isARSupported()) {
    logger.log('AR not supported on this device/browser');
    deviceUnsupported();
    return;
  }

  const canvas = root.querySelector<HTMLCanvasElement>('#ar-canvas')!;

  startARSession(canvas).then((result) => {
    if (result === 'permission-denied') {
      logger.log('Camera permission denied during AR start');
      permissionDenied();
      return;
    }
    if (result === 'unsupported') {
      logger.log('Camera session failed to start, device unsupported');
      deviceUnsupported();
      return;
    }
    logger.log(`Camera ready, transitioning to SCANNING in ${appConfig.arFadeInMs} ms`);
    acquireWakeLock();
    const timer = window.setTimeout(() => arStarted(), appConfig.arFadeInMs);
    (root as HTMLElement & { _arStartTimer?: number })._arStartTimer = timer;
  });
}

/**
 * Cancel the pending AR_STARTING → SCANNING timer and stop the XR8 session.
 * Call when leaving AR states entirely (e.g. navigating to an error screen).
 */
export function teardownARSession(root: HTMLElement): void {
  const stored = (root as HTMLElement & { _arStartTimer?: number })._arStartTimer;
  if (stored !== undefined) {
    window.clearTimeout(stored);
    delete (root as HTMLElement & { _arStartTimer?: number })._arStartTimer;
  }
  releaseWakeLock();
  stopARSession();
}
